import { QueryClient } from '@tanstack/react-query'
import { OmniaTimeSeriesDto } from '@/core/models/types'
import { TrendDto } from '@/api_generated/api'

export enum Topic {
    notifySensor = 'notifySensor',
    notifyTimeSeries = 'notifyTimeSeries',
}

// A live session can run for days, so a series is windowed rather than grown without limit.
export const MAX_TREND_POINTS = 5000

export interface TimeSeriesLatestUpdate {
    timeseriesTableDbKey: string
    value: number
    lastReceived: string
}

export interface TrendValuePoint {
    time: string
    value: number
}

/**
 * Appends a live point to an already cached series. Returns the input untouched when there is
 * nothing cached: seeding a trend from a single point would leave the chart with one value and,
 * because the queries use refetchOnMount:false, stop the full series from ever being fetched.
 */
export function appendPointToTrends(
    old: TrendDto[] | undefined,
    point: TrendValuePoint,
    maxPoints = MAX_TREND_POINTS
): TrendDto[] | undefined {
    if (!old || old.length === 0) return old

    return old.map((trend, index) => {
        if (index !== 0) return trend

        const values = (
            Array.isArray((trend as { values?: TrendValuePoint[] }).values)
                ? (trend as unknown as { values: TrendValuePoint[] }).values
                : []
        ) as TrendValuePoint[]
        const last = values[values.length - 1]
        const appended = last?.time === point.time ? [...values.slice(0, -1), point] : [...values, point]

        return {
            ...trend,
            values: appended.length > maxPoints ? appended.slice(-maxPoints) : appended,
        } as TrendDto
    })
}

export function applyLatestToMeasurements(
    old: OmniaTimeSeriesDto[] | undefined,
    update: TimeSeriesLatestUpdate
): OmniaTimeSeriesDto[] | undefined {
    if (!old || old.length === 0) return old

    let changed = false
    const next = old.map((item) => {
        if (item.timeseriesTableDbKey !== update.timeseriesTableDbKey) return item
        changed = true
        return {
            ...item,
            latestValue: update.value,
            latestTime: update.lastReceived,
        }
    })

    return changed ? next : old
}

// Refetches only what is mounted; inactive entries are marked stale and refetched if they mount again.
export function invalidateAllQueries(queryClient: QueryClient): void {
    queryClient.invalidateQueries({ refetchType: 'active' }).catch((e) => {
        console.log(`failed to invalidate all queries: ${e.message}`)
    })
}
